import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Shield } from "lucide-react";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ProviderLogo } from "@/components/ProviderCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { INDUSTRIES, USE_CASES, CONNECTIVITY, COUNTRIES_SUGGESTED } from "@/lib/taxonomies";

export default function RequestQuote() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", country: "", industry: "",
    use_case: "", tracking_target: "", quantity: "", region_route: "",
    sensor_requirements: "", connectivity_preference: "", timeline: "", budget: "",
    message: "", consent: false,
  });

  useEffect(() => {
    // Resolve provider slugs/ids from URL
    const providerSlug = sp.get("provider");
    const ids = (sp.get("ids") || "").split(",").filter(Boolean);
    (async () => {
      const provs = [];
      if (providerSlug) {
        try {
          const { data } = await api.get(`/providers/slug/${providerSlug}`);
          provs.push(data.provider);
        } catch {}
      }
      if (ids.length > 0) {
        const { data } = await api.post("/providers/compare", { ids });
        (data.items || []).forEach((p) => { if (!provs.find((x) => x.id === p.id)) provs.push(p); });
      }
      setSelected(provs);
    })();
  }, [sp]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent checkbox to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        selected_provider_ids: selected.map((p) => p.id),
        source_page: window.location.pathname + window.location.search,
      };
      const { data } = await api.post("/leads", payload);
      setSubmitted(data.lead_id);
      toast.success("Quote request submitted. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 size={56} className="mx-auto text-emerald-500"/>
        <h1 className="font-display font-extrabold text-3xl mt-4">Quote request received</h1>
        <p className="mt-3 text-slate-600">
          Thank you. Your enquiry has been routed to the relevant providers and our team.
          You'll hear back within 1–2 business days.
        </p>
        <div className="mt-2 text-xs text-slate-400 font-mono">Reference: {submitted}</div>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/solutions" className="btn-secondary" data-testid="quote-back-to-search">Back to directory</Link>
          <Link to="/" className="btn-primary" data-testid="quote-home">Return home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Multi-provider</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-1">Request tracking solution quotes</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Tell us about your tracking project. Submit to selected providers or send a general request
          and our team will recommend a shortlist.
        </p>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="quote-form">
        <div className="card-elevated p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name *">
              <Input value={form.name} onChange={(e)=>update("name", e.target.value)} required data-testid="quote-name"/>
            </Field>
            <Field label="Company *">
              <Input value={form.company} onChange={(e)=>update("company", e.target.value)} required data-testid="quote-company"/>
            </Field>
            <Field label="Work email *">
              <Input type="email" value={form.email} onChange={(e)=>update("email", e.target.value)} required data-testid="quote-email"/>
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e)=>update("phone", e.target.value)} data-testid="quote-phone"/>
            </Field>
            <Field label="Country">
              <SelectInput value={form.country} onChange={(v)=>update("country", v)} options={COUNTRIES_SUGGESTED} placeholder="Select country" testid="quote-country"/>
            </Field>
            <Field label="Industry">
              <SelectInput value={form.industry} onChange={(v)=>update("industry", v)} options={INDUSTRIES} placeholder="Select industry" testid="quote-industry"/>
            </Field>
          </div>

          <div className="border-t border-slate-200 pt-6 grid sm:grid-cols-2 gap-4">
            <Field label="Primary use case">
              <SelectInput value={form.use_case} onChange={(v)=>update("use_case", v)} options={USE_CASES} placeholder="Select use case" testid="quote-usecase"/>
            </Field>
            <Field label="Connectivity preference">
              <SelectInput value={form.connectivity_preference} onChange={(v)=>update("connectivity_preference", v)} options={CONNECTIVITY} placeholder="Optional" testid="quote-connectivity"/>
            </Field>
            <Field label="What do you need to track?">
              <Input value={form.tracking_target} onChange={(e)=>update("tracking_target", e.target.value)} placeholder="e.g. Pharma pallets, returnable containers, fleet trailers…" data-testid="quote-target"/>
            </Field>
            <Field label="Volume (devices, assets or shipments)">
              <Input value={form.quantity} onChange={(e)=>update("quantity", e.target.value)} placeholder="e.g. 500 devices, 2,000 shipments/year" data-testid="quote-quantity"/>
            </Field>
            <Field label="Required region / route">
              <Input value={form.region_route} onChange={(e)=>update("region_route", e.target.value)} placeholder="e.g. EU → US cold chain" data-testid="quote-route"/>
            </Field>
            <Field label="Temperature / sensor requirements">
              <Input value={form.sensor_requirements} onChange={(e)=>update("sensor_requirements", e.target.value)} placeholder="e.g. 2–8 °C with shock alerts" data-testid="quote-sensors"/>
            </Field>
            <Field label="Timeline">
              <SelectInput value={form.timeline} onChange={(v)=>update("timeline", v)}
                options={["Immediate (0-30 days)","1-3 months","3-6 months","6-12 months","Exploring"]}
                placeholder="Select timeline" testid="quote-timeline"/>
            </Field>
            <Field label="Budget range (optional)">
              <SelectInput value={form.budget} onChange={(v)=>update("budget", v)}
                options={["< $10k","$10k - $50k","$50k - $250k","$250k - $1M","$1M+","Undecided"]}
                placeholder="Optional" testid="quote-budget"/>
            </Field>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <Field label="Additional message">
              <Textarea rows={4} value={form.message} onChange={(e)=>update("message", e.target.value)} placeholder="Describe your project, requirements or constraints." data-testid="quote-message"/>
            </Field>
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-600">
            <Checkbox checked={form.consent} onCheckedChange={(v)=>update("consent", !!v)} data-testid="quote-consent" />
            <span>I consent to my information being shared with Strategic Tracking and selected providers for the purpose of this enquiry.</span>
          </label>

          <button type="submit" disabled={submitting} className="btn-accent text-base w-full justify-center h-12" data-testid="quote-submit">
            {submitting ? "Submitting…" : "Submit quote request"} <ArrowRight size={16}/>
          </button>
        </div>

        <aside>
          <div className="card-elevated p-6 sticky top-20">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Sending to</div>
            {selected.length === 0 ? (
              <div className="text-sm text-slate-600">
                <p>No specific providers selected. We'll route your enquiry to relevant providers and our team.</p>
                <Link to="/solutions" className="mt-3 inline-flex items-center gap-1 text-[#002FA7] font-semibold text-sm">
                  Select providers <ArrowRight size={13}/>
                </Link>
              </div>
            ) : (
              <ul className="space-y-3">
                {selected.map((p) => (
                  <li key={p.id} className="flex items-center gap-3" data-testid={`quote-selected-${p.slug}`}>
                    <ProviderLogo provider={p} size={36}/>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-sm text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.hq_country}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-5 border-t border-slate-200 pt-4 text-xs text-slate-500 inline-flex items-start gap-2">
              <Shield size={14} className="mt-0.5 text-slate-400"/>
              <span>Your data is sent securely. We never sell buyer enquiries.</span>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function SelectInput({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder}/></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
